"use client";

import { apiClient } from "@/services/apiClient";

export type BookingSelectOption = {
  value: string;
  label: string;
  isOther?: boolean;
};

export type BookingCategoryOption = BookingSelectOption & {
  isBooked: boolean;
  /**
   * When true the category is grouped under the selector's "Other" option
   * instead of being listed directly. Distinct from BookingSelectOption's
   * `isOther`, which marks the free-text "Other" choice on unrelated selects
   * (e.g. "How did you hear about us?").
   */
  showInOther: boolean;
};

export type FreeTrialSlot = {
  start: string;
  end: string;
  start_utc: string;
  end_utc: string;
  teacher_id: number;
  isReserved: boolean;
};

type LanguageResponseItem = {
  code: string;
  name: string;
};

type HearAboutUsResponseItem = {
  id: number;
  name: string;
};

export async function confirmFreeTrialBookingApi(payload: {
  attendeeId: string;
  categoryId: string;
  languageCode: string;
  hearAboutUsId: string;
  hearAboutUsOther?: string;
  teacherId: number;
  startUtc: string;
  endUtc: string;
  locale: string;
  notes?: string;
}): Promise<any> {
  const response = await apiClient.post(`/api/book/free-trial/confirm?locale=${payload.locale}`, {
    attendee_id: Number(payload.attendeeId),
    category_id: Number(payload.categoryId),
    language_code: payload.languageCode,
    hear_about_us_id: Number(payload.hearAboutUsId),
    hear_about_us_other: payload.hearAboutUsOther,
    teacher_id: payload.teacherId,
    start_utc: payload.startUtc,
    end_utc: payload.endUtc,
    notes: payload.notes,
  });

  return response.data;
}

export async function getFreeTrialMetadataApi(
  locale: string,
  attendeeId?: string
): Promise<{
  categories: BookingCategoryOption[];
  languages: BookingSelectOption[];
  hearAboutUs: BookingSelectOption[];
  hasActiveFreeTrial: boolean;
}> {
  const response = await apiClient.get("/api/book/free-trial/metadata?locale=" + locale, {
    params: { locale, attendee_id: attendeeId },
  });

  const { categories, languages, hear_about_us, has_active_free_trial } = response.data;

  return {
    categories: categories.map((cat: any) => ({
      value: String(cat.id),
      label: cat.name,
      isBooked: cat.is_booked,
      showInOther: cat.show_in_other === true,
    })),
    languages: languages.map((lang: any) => ({
      value: lang.code,
      label: lang.name,
    })),
    hearAboutUs: hear_about_us.map((item: any) => ({
      value: String(item.id),
      label: item.name,
      isOther: Boolean(item.is_other),
    })),
    hasActiveFreeTrial: Boolean(has_active_free_trial),
  };
}

/**
 * The languages a free trial can actually be booked in for this attendee +
 * category: only those spoken by a teacher the slot list would match (backend
 * FreeTrialService::getEligibleLanguages()). The picker calls this whenever the
 * category changes so it never offers a language whose slots then come back
 * empty. Distinct from getFreeTrialMetadataApi()'s `languages`, which is every
 * active language and is not used to drive the picker.
 */
export async function getFreeTrialLanguagesApi(payload: {
  attendeeId: string;
  categoryId: string;
  locale: string;
}): Promise<BookingSelectOption[]> {
  const response = await apiClient.get("/api/book/free-trial/languages", {
    params: {
      attendee_id: Number(payload.attendeeId),
      category_id: Number(payload.categoryId),
      locale: payload.locale,
    },
  });

  const { languages } = response.data;

  if (!Array.isArray(languages)) {
    throw new Error("Invalid free trial languages response");
  }

  return languages.map((lang: any) => ({
    value: lang.code,
    label: lang.name,
  }));
}

export async function getFreeTrialSlotsApi(payload: {
  attendeeId: string;
  categoryId: string;
  languageCode: string;
  hearAboutUsId: string;
  hearAboutUsOther?: string;
  selectedDate: string;
  locale: string;
}): Promise<FreeTrialSlot[]> {
  const response = await apiClient.post(`/api/book/free-trial?locale=${payload.locale}`, {
    attendee_id: Number(payload.attendeeId),
    category_id: Number(payload.categoryId),
    language_code: payload.languageCode,
    hear_about_us_id: Number(payload.hearAboutUsId),
    hear_about_us_other: payload.hearAboutUsOther,
    selected_date: payload.selectedDate,
  });

  return normalizeSlots(response.data);
}

/**
 * Per-day bookability for the month CalendarPicker, keyed by 'yyyy-MM-dd' -
 * powers the calendar's disabled-day state so a day only looks clickable
 * when the backend (SchedulingService::getFreeTrialAvailabilityByDate())
 * found at least one slot that would genuinely survive both the real slot
 * search's eligibility rules and the reserve-cap/tier ladder, not just "some
 * teacher happens to have no conflict".
 */
export async function getFreeTrialAvailabilityApi(payload: {
  attendeeId: string;
  categoryId: string;
  languageCode: string;
  startDate: string;
  endDate: string;
  locale: string;
}): Promise<Record<string, boolean>> {
  const response = await apiClient.get(`/api/book/free-trial/availability?locale=${payload.locale}`, {
    params: {
      attendee_id: Number(payload.attendeeId),
      category_id: Number(payload.categoryId),
      language_code: payload.languageCode,
      start_date: payload.startDate,
      end_date: payload.endDate,
    },
  });

  return normalizeAvailability(response.data);
}

function normalizeAvailability(payload: unknown): Record<string, boolean> {
  if (!isRecord(payload) || !isRecord(payload.data)) {
    throw new Error("Invalid free trial availability response");
  }

  const result: Record<string, boolean> = {};

  for (const [date, bookable] of Object.entries(payload.data)) {
    result[date] = Boolean(bookable);
  }

  return result;
}

function normalizeSlots(payload: unknown): FreeTrialSlot[] {
  if (
    !isRecord(payload) ||
    !Array.isArray(payload.data)
  ) {
    throw new Error("Invalid free trial slots response");
  }

  return payload.data
    .filter(isSlotResponseItem)
    .map((slot: any) => ({
      start: slot.start,
      end: slot.end,
      start_utc: slot.start_utc,
      end_utc: slot.end_utc,
      teacher_id: slot.teacher_id,
      isReserved: slot.is_reserved,
    }));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isSlotResponseItem(
  value: unknown
): value is { start: string; end: string; start_utc: string; end_utc: string; teacher_id: number; is_reserved: boolean } {
  return (
    isRecord(value) &&
    isNonEmptyString(value.start) &&
    isNonEmptyString(value.end) &&
    isNonEmptyString(value.start_utc) &&
    isNonEmptyString(value.end_utc) &&
    typeof value.teacher_id === "number" &&
    typeof value.is_reserved === "boolean"
  );
}
